import { ExpandLess, ExpandMore } from '@mui/icons-material';
import {
  alpha,
  Box,
  Button,
  Checkbox,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  SelectChangeEvent,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import BellCurve from 'icons/BellCurve';
import { useState, useMemo } from 'react';
import { useRecoilValue } from 'recoil';
import { timezoneState } from 'states';
import {
  GREEK_IDX,
  OPTION_IDX,
  RawGreeksDataMap,
  RawStatsData,
  VolSkewReferenceLine,
} from 'types';
import {
  PositionLeg,
  IVAdjustmentSettings,
  OptionLegGreeks,
  StrikeDeltaObject,
} from 'types/optionsCalculator';
import { dayjs, getQueryDate, formatAsPercentage } from 'util/shared';
import {
  extractTermStructureData,
  convertToDaysTillExpiry,
  getExpirationLabel,
} from 'util/iVol';
import {
  matchesDefaultIVSettings,
  shortIdGenerator,
} from 'util/optionsCalculator';
import EditableOptionLeg from './EditableOptionLeg';
import IVSettings from './IVSettings';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import PrecisionManufacturingIcon from '@mui/icons-material/PrecisionManufacturing';
import { SGTooltip } from 'components/core';

interface GroupedOptionLegsProps {
  legs: PositionLeg[];
  legIVs: Map<number, number>;
  adjustedLegIVs?: Map<number, number>;
  greeks?: RawGreeksDataMap;
  greeksLoading?: boolean;
  stats?: RawStatsData;
  expiration: number;
  expirationOptions: number[];
  strikeDeltaOptions: StrikeDeltaObject[];
  onAddLeg: (leg: PositionLeg) => void;
  onEditLeg: (
    legId: number,
    updatedLeg: Partial<PositionLeg>,
    shouldUseDefaultPrice?: boolean,
  ) => void;
  onDeleteLeg: (legId: number) => void;
  onDeleteLegsBulk: (legIds: number[]) => void;
  onGroupExpirationChange: (newExpiration: number, legIds: number[]) => void;
  ivSettings: IVAdjustmentSettings;
  onUpdateIVSettings: (settings: Partial<IVAdjustmentSettings>) => void;
  currentPrice: number | undefined;
  sym: string;
}

const GroupedOptionLegs = ({
  legs,
  legIVs,
  adjustedLegIVs,
  greeks,
  greeksLoading = false,
  stats,
  expiration,
  expirationOptions,
  strikeDeltaOptions,
  onAddLeg,
  onEditLeg,
  onDeleteLeg,
  onDeleteLegsBulk,
  onGroupExpirationChange,
  ivSettings,
  onUpdateIVSettings,
  currentPrice,
  sym,
}: GroupedOptionLegsProps) => {
  const theme = useTheme();
  const currentTimezone = useRecoilValue(timezoneState);
  const [expanded, setExpanded] = useState<boolean>(true);
  const [showIvSettings, setShowIvSettings] = useState<boolean>(false);
  const [localIvSettings, setLocalIvSettings] =
    useState<IVAdjustmentSettings>(ivSettings);
  const [expirySelectorOpen, setExpirySelectorOpen] = useState(false);

  const [currentReferenceLine, setCurrentReferenceLine] =
    useState<VolSkewReferenceLine>(VolSkewReferenceLine.MedianIV);

  const handleRemoveAllLegs = () => {
    onDeleteLegsBulk(legs.map((leg) => leg.leg_id ?? 0));
  };

  const handleToggleIvSettings = () => setShowIvSettings((prev) => !prev);

  const handleUpdateIvSettings = (settings: IVAdjustmentSettings) => {
    setLocalIvSettings(settings);
  };

  const handleUpdateCurrentReferenceLine = (line: VolSkewReferenceLine) => {
    setCurrentReferenceLine(line);
  };

  const handleSaveIvSettings = () => {
    onUpdateIVSettings(localIvSettings);
    setShowIvSettings(false);
  };

  const handleAddLeg = () => {
    // Use the first leg in the group as a template
    onAddLeg({
      ...legs?.[0],
      leg_id: shortIdGenerator(),
      expiry: expiration,
    });
  };

  const handleOpen = () => {
    setExpirySelectorOpen(true);
  };

  const handleClose = () => {
    setExpirySelectorOpen(false);
  };

  const handleExpirationChange = (event: SelectChangeEvent<number>) => {
    const newExpiration = Number(event.target.value);

    // Validate the date
    if (!newExpiration) {
      return;
    }

    const dateObj = dayjs(newExpiration);
    const today = dayjs();

    if (!dateObj.isValid() || dateObj.isBefore(today, 'day')) {
      return;
    }

    onGroupExpirationChange(
      newExpiration,
      legs.map((leg) => leg.leg_id),
    );

    setExpirySelectorOpen(false);
  };

  const getLegGreeks = (leg: PositionLeg): OptionLegGreeks | null => {
    if (
      currentPrice == null ||
      greeks == null ||
      greeks[expiration] == null ||
      greeks[expiration][leg.strike] == null
    ) {
      return null;
    }

    const greeksForLeg =
      greeks[expiration][leg.strike][
        leg.option_type === 'C' ? OPTION_IDX.CALL : OPTION_IDX.PUT
      ];

    // Extract and parse all key data points from raw greeks
    return {
      delta: greeksForLeg[GREEK_IDX.DELTA],
      gamma: greeksForLeg[GREEK_IDX.GAMMA],
      vega: greeksForLeg[GREEK_IDX.VEGA],
      theta: greeksForLeg[GREEK_IDX.THETA],
      iv: legIVs.get(leg.leg_id) ?? 0,
      price:
        ((greeksForLeg[GREEK_IDX.BID] ?? 0) +
          (greeksForLeg[GREEK_IDX.ASK] ?? 0)) /
        2.0,
      adjustedIv: adjustedLegIVs?.get(leg.leg_id),
    };
  };

  const currentAtmIV: number | undefined = useMemo(() => {
    if (stats == null || greeks == null || currentPrice == null) {
      return undefined;
    }

    const queryDate = getQueryDate(true, currentTimezone);
    const key: string = queryDate.format('YYYY-MM-DD');

    const termStructureData = extractTermStructureData(
      greeks,
      queryDate,
      key,
      stats,
      currentPrice,
      true,
    );

    return termStructureData.find((ts) => ts.expirationDate === expiration)?.[
      key
    ] as number | undefined;
  }, [stats, greeks, currentPrice, expiration, currentTimezone]);

  const disabledExpirationOptions = useMemo(() => {
    if (greeks == null) {
      return [];
    }

    return expirationOptions.filter((utcTime) => {
      const rawGreeksObject = greeks[utcTime];
      const relatedStrikes =
        rawGreeksObject != null ? Object.keys(rawGreeksObject).map(Number) : [];

      // Check if all leg strikes are available in this expiration's data
      const legStrikes = legs.map((leg) => leg.strike);
      const hasAllLegStrikes = legStrikes.every((strike) =>
        relatedStrikes.includes(strike),
      );
      return !hasAllLegStrikes;
    });
  }, [legs, greeks, expirationOptions]);

  const isExpired = useMemo(() => {
    return dayjs().isAfter(dayjs(expiration), 'day');
  }, [expiration]);

  if (legs.length === 0) {
    return null;
  }

  return (
    <Box sx={{ height: '100%' }}>
      <Stack
        sx={{
          flexDirection: {
            xs: 'column',
            sm: 'row',
          },
          py: 2,
          px: {
            xs: 2,
            sm: 4,
          },
          justifyContent: 'space-between',
          alignItems: {
            xs: 'flex-start',
            sm: 'center',
          },
          backgroundColor: alpha(theme.palette.primary.main, 0.3),
          borderTopRightRadius: '8px',
          borderTopLeftRadius: '8px',
          gap: {
            xs: 2,
            sm: 0,
          },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            width: { xs: '100%', sm: 'auto' },
          }}
        >
          <IconButton
            color="primary"
            size="small"
            onClick={() => setExpanded(!expanded)}
          >
            {expanded ? <ExpandLess /> : <ExpandMore />}
          </IconButton>
          <FormControl size="small" sx={{ width: { xs: '100%', lg: 140 } }}>
            <InputLabel id="expiry-date-select-label">Expiration</InputLabel>
            <Select
              labelId="expiry-date-select-label"
              id="expiry-date-select"
              open={expirySelectorOpen}
              onClose={handleClose}
              onOpen={handleOpen}
              onChange={handleExpirationChange}
              value={expiration}
              size="small"
              input={
                <OutlinedInput
                  label="Expiration"
                  sx={{
                    svg: {
                      color: alpha(theme.palette.primary.main, 0.5),
                    },
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: alpha(theme.palette.primary.main, 0.5),
                    },
                    '& .MuiOutlinedInput-input': {
                      fontSize: '12px',
                    },
                  }}
                />
              }
              renderValue={(selected: number) =>
                `${dayjs(selected).format('YYYY-MM-DD')}`
              }
              MenuProps={{
                sx: {
                  zIndex: 10_001,
                  '& .MuiSelect-icon': {
                    color: theme.palette.text.primary,
                  },
                },
                PaperProps: {
                  style: {
                    width: '140px',
                    maxHeight: '250px',
                  },
                },
              }}
            >
              {expirationOptions.map((utcTime: number) => {
                return (
                  <MenuItem
                    key={utcTime}
                    value={utcTime}
                    sx={{
                      padding: '10px',
                    }}
                    disabled={disabledExpirationOptions.includes(utcTime)}
                  >
                    <Checkbox
                      checked={expiration === utcTime}
                      style={{
                        padding: '0px 10px 0px 0px',
                      }}
                    />
                    <ListItemText
                      primary={`${getExpirationLabel(utcTime.toString())}`}
                      secondary={`${convertToDaysTillExpiry(
                        dayjs().valueOf(),
                        utcTime,
                      )} DTE`}
                      primaryTypographyProps={{
                        fontSize: '13px',
                      }}
                    />
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>

          {/* ATM IV Display */}
          <SGTooltip title="The current implied volatility for an at-the-money option for the ticker selected">
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                ml: 2,
                px: 3,
                py: 1,
                backgroundColor: alpha(theme.palette.info.main, 0.1),
                borderRadius: '16px',
                border: `1px solid ${alpha(theme.palette.info.main, 0.3)}`,
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  fontSize: '11px',
                  fontWeight: 600,
                  color: theme.palette.text.secondary,
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                }}
              >
                Current ATM IV:
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  fontSize: '13px',
                  fontWeight: 700,
                  color: theme.palette.text.primary,
                }}
              >
                {currentAtmIV != null
                  ? `${formatAsPercentage(currentAtmIV)}`
                  : 'N/A'}
              </Typography>
            </Box>
          </SGTooltip>
        </Box>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            flexWrap: { xs: 'wrap', sm: 'nowrap' },
            width: { xs: '100%', sm: 'auto' },
            justifyContent: { xs: 'flex-start', sm: 'flex-end' },
          }}
        >
          <Box sx={{ position: 'relative', display: 'inline-block' }}>
            <Button
              variant="outlined"
              size="small"
              color="success"
              onClick={handleToggleIvSettings}
              sx={{
                borderRadius: '4px',
                '&:hover': {
                  backgroundColor: alpha(theme.palette.secondary.main, 0.1),
                },
                fontSize: '12px',
                textTransform: 'none',
                minWidth: { xs: '110px', sm: 'auto' },
              }}
              startIcon={<BellCurve />}
              disabled={isExpired}
            >
              IV Settings
            </Button>
            {ivSettings != null && !matchesDefaultIVSettings(ivSettings) && (
              <Box
                sx={{
                  position: 'absolute',
                  top: -6,
                  right: -6,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 20,
                  height: 20,
                  backgroundColor: theme.palette.warning.main,
                  borderRadius: '50%',
                  border: `2px solid ${theme.palette.background.paper}`,
                  boxShadow: `0 2px 8px ${alpha(theme.palette.warning.main, 0.4)}`,
                  transition: 'all 0.2s ease-in-out',
                  '&:hover': {
                    transform: 'scale(1.1)',
                    boxShadow: `0 4px 12px ${alpha(theme.palette.warning.main, 0.6)}`,
                  },
                }}
              >
                <PrecisionManufacturingIcon
                  sx={{
                    fontSize: 12,
                    color: theme.palette.warning.contrastText,
                  }}
                />
              </Box>
            )}
          </Box>

          <Button
            startIcon={<AddIcon />}
            variant="outlined"
            color="primary"
            size="small"
            onClick={handleAddLeg}
            sx={{
              borderRadius: '4px',
              fontSize: '12px',
              textTransform: 'none',
              minWidth: { xs: '100px', sm: 'auto' },
            }}
          >
            Add Leg
          </Button>
          <Button
            startIcon={<DeleteIcon />}
            variant="outlined"
            color="error"
            size="small"
            onClick={handleRemoveAllLegs}
            sx={{
              borderRadius: '4px',
              fontSize: '12px',
              textTransform: 'none',
              minWidth: { xs: '110px', sm: 'auto' },
            }}
          >
            Remove Legs
          </Button>
        </Box>
      </Stack>

      <Collapse in={expanded}>
        <Box
          sx={{
            pl: '32px',
          }}
        >
          <Stack
            sx={{
              backgroundColor: alpha(theme.palette.primary.main, 0.1),
              borderBottomRightRadius: '8px',
              borderBottomLeftRadius: '8px',
            }}
            divider={
              <Divider
                sx={{ borderColor: alpha(theme.palette.primary.main, 0.5) }}
              />
            }
          >
            {legs.map((leg) => (
              <EditableOptionLeg
                key={leg.leg_id}
                leg={leg}
                legGreeks={getLegGreeks(leg)}
                greeksLoading={greeksLoading}
                onEditLeg={onEditLeg}
                onDeleteLeg={onDeleteLeg}
                expirationOptions={expirationOptions}
                disabledExpirationOptions={disabledExpirationOptions}
                strikeDeltaOptions={strikeDeltaOptions}
              />
            ))}
          </Stack>
        </Box>
      </Collapse>

      <Dialog
        open={showIvSettings}
        onClose={handleToggleIvSettings}
        maxWidth="lg"
        fullWidth
        key={`${expiration}-${showIvSettings}`}
      >
        <DialogTitle>IV Settings</DialogTitle>
        <DialogContent>
          <Box
            sx={{
              transition: '0.5s',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <IVSettings
              sym={sym}
              ivSettings={localIvSettings}
              onUpdateIvSettings={handleUpdateIvSettings}
              defaultExpiration={expiration}
              currentReferenceLine={currentReferenceLine}
              onUpdateCurrentReferenceLine={handleUpdateCurrentReferenceLine}
              currentAtmIV={currentAtmIV}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            variant="text"
            onClick={handleToggleIvSettings}
            sx={{ textTransform: 'none' }}
          >
            Close
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveIvSettings}
            sx={{ textTransform: 'none' }}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default GroupedOptionLegs;
